import React, { useState, useEffect, useMemo } from "react";
import { Container } from "reactstrap";
import {
  DownloadCSV,
  FetchPendingAccountHolders,
  FetchUsers,
  SearchRecords,
  ChangeAdminApproval,
} from "../../apis/User";
import { Search, Upload } from "lucide-react";
import Paginate from "../../components/shared/paginate";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const VerifyUser = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.MANAGE_USERS);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [role, setRole] = useState("all");
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  const limit = "10";
  const [dataInCSV, setDataInCSV] = useState("");

  const columns = useMemo(
    () => [
      "Profile ID",
      "Name",
      "Email",
      "Holder type",
      "Admin approval",
      "Individual nationality type",
      "Individual DOB",
      "Individual national ID",
      "Individual iqama number",
      "Individual passport number",
      "Visitor nationality",
      "Company name",
      "CR number",
      "Responsible type",
      "Responsible national ID",
      "Responsible iqama number",
      "Responsible passport number",
      "Created date",
    ],
    [],
  );

  const fetchUsers = async () => {
    try {
      const response = await FetchPendingAccountHolders(currentPage, limit);
      if (response.success) {
        setFilteredUsers(response.data.users);
        setTotalPages(Math.ceil(response.data.totalUsers / limit));
      } else {
        toast.error("Failed to fetch users. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to fetch users. Please try again later.");
    }
  };
  useEffect(() => {
    DownloadCSV().then((res) => {
      setDataInCSV(res);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRole = (e) => {
    setRole(e.target.value);
  };

  useEffect(() => {
    if (keyword || role !== "all") {
      searchUsers();
    } else {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, keyword, role]);

  const searchUsers = async () => {
    try {
      const response = await SearchRecords(keyword, currentPage, limit, role);
      if (response.success) {
        setFilteredUsers(response.data.results);
        setTotalPages(response.data.totalPages);
      } else {
        toast.error("Failed to search users. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to search users. Please try again later.");
    }
  };

  const handleAdminApprovalChange = async (userId, value) => {
    const previous = filteredUsers;
    // optimistic UI update
    setFilteredUsers((prev) =>
      prev.map((user) =>
        user._id === userId
          ? {
              ...user,
              accountHolder: {
                ...user.accountHolder,
                adminApproval: value,
              },
            }
          : user,
      ),
    );

    try {
      const res = await ChangeAdminApproval(userId, value);
      if (!res.success) {
        toast.error("Failed to update admin approval status.");
        setFilteredUsers(previous);
      } else {
        toast.success("Admin approval status updated.");
      }
    } catch (error) {
      toast.error("Failed to update admin approval status.");
      setFilteredUsers(previous);
    }
  };
  return (
    <React.Fragment>
      <div className="page-content">
        <Container
          //  h-[calc(100vh-130px)]
          className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]"
          fluid
        >
          <div className="p-6 flex flex-col gap-6 ">
            <h1 className="text-2xl dark:text-[#c7d8eb]">Verify users</h1>
            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full grid lg:grid-cols-[minmax(320px,_1fr)_160px_1fr] gap-4 p-4">
                <div className="relative border border-gray-500 dark:border-white rounded-md w-full">
                  <Search
                    size={16}
                    className="absolute left-2 bottom-1/2 translate-y-1/2"
                  />
                  <input
                    className="pl-8 pr-6 py-2 text-base rounded-md w-full dark:bg-darkBg2"
                    type="search"
                    placeholder="Search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </div>
                <select
                  value={role}
                  onChange={handleRole}
                  className="p-2 border border-gray-500 dark:border-white rounded-md dark:bg-darkBg2"
                >
                  <option value={"all"}>All</option>
                  <option value={"host"}>Host</option>
                  <option value={"guest"}>Guest</option>
                </select>
                <div className="flex lg:justify-self-end">
                  <a
                    href={`data:text/csv;charset=utf-8,${escape(dataInCSV)}`}
                    download="users-list.csv"
                  >
                    <div className="flex items-center gap-2">
                      Export data into CSV
                      <div className="shrink-0 bg-white p-1">
                        <Upload className="" size={16} />
                      </div>
                    </div>
                  </a>
                </div>
              </div>
              <div className="overflow-x-auto ">
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className={`${
                        index === 0 ? "w-[12%]" : "w-[11%]"
                      } min-w-[130px] content-center border-y border-gray-500 dark:border-white break-words py-2 text-center font-semibold`}
                    >
                      {column}
                    </div>
                  ))}
                </div>
                {(filteredUsers &&
                  filteredUsers.length > 0 &&
                  filteredUsers.map((user, index) => (
                    <>
                      <div
                        key={`user-desktop-${index}`}
                        className="hidden lg:flex"
                      >
                        <div className="w-[12%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user._id}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.email}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.holderType || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {canManage ? (
                            <select
                              value={
                                user.accountHolder?.adminApproval ||
                                "not_verified"
                              }
                              onChange={(e) =>
                                handleAdminApprovalChange(
                                  user._id,
                                  e.target.value,
                                )
                              }
                              className="p-1 border border-gray-500 dark:border-white rounded-md dark:bg-darkBg2 text-xs"
                            >
                              <option value="not_verified">Not verified</option>
                              <option value="on_hold">On hold</option>
                              <option value="verified">Verified</option>
                              <option value="rejected">Rejected</option>
                            </select>
                          ) : (
                            <span className="text-xs">
                              {user.accountHolder?.adminApproval ||
                                "not_verified"}
                            </span>
                          )}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.individual?.nationalityType ||
                            "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.individual?.dob
                            ? new Date(user.accountHolder.individual.dob)
                                .toISOString()
                                .slice(0, 10)
                            : "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.individual?.nationalId || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.individual?.iqamaNumber || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.individual?.passportNumber ||
                            "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.individual?.visitorNationality ||
                            "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.entity?.companyName || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.entity?.crNumber || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.entity?.responsible
                            ?.responsibleType || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.entity?.responsible
                            ?.nationalId || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.accountHolder?.entity?.responsible
                            ?.iqamaNumber || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark-border-white break-words py-2 text-center">
                          {user.accountHolder?.entity?.responsible
                            ?.passportNumber || "-"}
                        </div>
                        <div className="w-[11%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {user.createdAt?.toString()?.slice(0, 10)}
                        </div>
                      </div>
                      <div
                        key={`user-mobile-${index}`}
                        className="block lg:hidden mb-6 border-x border-y border-gray-500 dark:border-white"
                      >
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Profile ID</div>
                          <div>{user._id}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Name</div>
                          <div>
                            {user.firstName} {user.lastName}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Email</div>
                          <div>{user.email}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Holder type</div>
                          <div>{user.accountHolder?.holderType || "-"}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Admin approval</div>
                          <div>
                            {canManage ? (
                              <select
                                value={
                                  user.accountHolder?.adminApproval ||
                                  "not_verified"
                                }
                                onChange={(e) =>
                                  handleAdminApprovalChange(
                                    user._id,
                                    e.target.value,
                                  )
                                }
                                className="p-1 border border-gray-500 dark:border-white rounded-md dark:bg-darkBg2 text-xs"
                              >
                                <option value="not_verified">
                                  Not verified
                                </option>
                                <option value="on_hold">On hold</option>
                                <option value="verified">Verified</option>
                                <option value="rejected">Rejected</option>
                              </select>
                            ) : (
                              <span className="text-xs">
                                {user.accountHolder?.adminApproval ||
                                  "not_verified"}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">
                            Individual nationality type
                          </div>
                          <div>
                            {user.accountHolder?.individual?.nationalityType ||
                              "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Individual DOB</div>
                          <div>
                            {user.accountHolder?.individual?.dob
                              ? new Date(user.accountHolder.individual.dob)
                                  .toISOString()
                                  .slice(0, 10)
                              : "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">
                            Individual national ID
                          </div>
                          <div>
                            {user.accountHolder?.individual?.nationalId || "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">
                            Individual iqama number
                          </div>
                          <div>
                            {user.accountHolder?.individual?.iqamaNumber || "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">
                            Individual passport number
                          </div>
                          <div>
                            {user.accountHolder?.individual?.passportNumber ||
                              "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Visitor nationality</div>
                          <div>
                            {user.accountHolder?.individual
                              ?.visitorNationality || "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Company name</div>
                          <div>
                            {user.accountHolder?.entity?.companyName || "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">CR number</div>
                          <div>
                            {user.accountHolder?.entity?.crNumber || "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Responsible type</div>
                          <div>
                            {user.accountHolder?.entity?.responsible
                              ?.responsibleType || "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">
                            Responsible national ID
                          </div>
                          <div>
                            {user.accountHolder?.entity?.responsible
                              ?.nationalId || "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">
                            Responsible iqama number
                          </div>
                          <div>
                            {user.accountHolder?.entity?.responsible
                              ?.iqamaNumber || "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">
                            Responsible passport number
                          </div>
                          <div>
                            {user.accountHolder?.entity?.responsible
                              ?.passportNumber || "-"}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Created date</div>
                          <div>{user.createdAt?.toString()?.slice(0, 10)}</div>
                        </div>
                      </div>
                    </>
                  ))) || (
                  <div className="flex justify-center items-center h-20 border-x border-b border-gray-500 dark:border-white">
                    No matching records found.
                  </div>
                )}
              </div>
            </div>
            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default VerifyUser;
